/**
 * The catalog — every schema, every record type, and the staff directory — loaded as one
 * snapshot, held here, and wired into the reference registries the field layer
 * resolves through.
 *
 * A copy knows which version it is by two values the database keeps:
 * `last_updated` (the newest `updated_at` across schemas and types, moved by
 * any insert or update) and `schema_count` (moved by a schema delete). Types
 * are never deleted: Hasura grants no delete on `types`, so a type removed
 * by hand in SQL stays in every copy until a reload. `check()` reads those two
 * and compares; `refresh()` reloads only when they differ, and skips even the
 * check when the copy was checked within `maxAgeMs`.
 *
 * Known limit: the `updated_at` triggers stamp `now()`, the transaction's START
 * time. A long transaction (a migration rewriting `schemas.fields`) can commit
 * with a timestamp older than one a check already saw, and that check misses
 * it. `refresh({ force: true })` reloads without asking.
 *
 * A type holds its default schema as `schema_id` only. The published type's
 * `schema` is resolved from the schemas held here, so a schema write is seen
 * through every type that defaults to it — nothing is held twice.
 *
 * Writes go through the schemas and types services, which hand the written row
 * to `written`: it updates the copy at once, marks it unchecked, and notifies
 * `onChange`. The next refresh reloads. Stamping the copy with the status read
 * after the write would be wrong: that status also covers every write others
 * made since the last check, which this copy does not hold, and every later
 * check would call it fresh.
 */
import { buildDirectory } from "../directory.js"
import { fetchCatalog, fetchCatalogStatus } from "../gql/catalog.js"
import type { Query } from "../index.js"
import { registerSchemas, type Schema } from "../registries/schemas.js"
import { registerTypes } from "../registries/types.js"
import { registerUsers, type User } from "../registries/users.js"
import { asSchemaRow, type SchemaRow } from "./schemas.js"
import type { TypeRow } from "./types.js"

export interface CatalogStatus {
  last_updated: string | null
  schema_count: number
}

/** One catalog snapshot: the rows as fetched, and the status they were fetched at. */
export interface Catalog {
  schemas: SchemaRow[]
  types: TypeRow[]
  users: User[]
  status: CatalogStatus
}

/** A change a schema or type write hands to the catalog. */
export type CatalogChange = { schema: SchemaRow } | { removedSchemaId: number } | { type: TypeRow }

/** The status of a copy that holds writes no check has confirmed. */
const UNCHECKED: CatalogStatus = { last_updated: null, schema_count: -1 }

/** How long a checked copy is trusted without asking again. */
export const CATALOG_MAX_AGE_MS = 10_000

type StatusData = {
  schemas_aggregate?: {
    aggregate?: { count?: number | null; max?: { updated_at?: string | null } | null } | null
  } | null
  types_aggregate?: { aggregate?: { max?: { updated_at?: string | null } | null } | null } | null
}

function toStatus(data: StatusData | null | undefined): CatalogStatus {
  const schemasMax = data?.schemas_aggregate?.aggregate?.max?.updated_at ?? null
  const typesMax = data?.types_aggregate?.aggregate?.max?.updated_at ?? null
  // ISO timestamps from one database compare correctly as numbers of ms.
  const newest = [schemasMax, typesMax]
    .filter((t): t is string => t != null)
    .sort((a, b) => Date.parse(b) - Date.parse(a))[0]
  return {
    last_updated: newest ?? null,
    schema_count: data?.schemas_aggregate?.aggregate?.count ?? 0,
  }
}

const sameStatus = (a: CatalogStatus, b: CatalogStatus): boolean =>
  a.schema_count === b.schema_count &&
  (a.last_updated === b.last_updated ||
    (a.last_updated != null &&
      b.last_updated != null &&
      Date.parse(a.last_updated) === Date.parse(b.last_updated)))

/** Reject when `promise` has not settled within `ms`. The request itself is not aborted. */
function withTimeout<T>(promise: Promise<T>, ms: number | undefined): Promise<T> {
  if (!ms) return promise
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`catalog request timed out after ${ms} ms`)), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

export interface CatalogOptions {
  query: Query
  /** The host's own user lookup. When given, the catalog neither fetches nor
   *  registers users — the frontend keeps a richer user row than the catalog's. */
  users?: (id: number) => User | null
  /** Fail a catalog or status request that hasn't answered in this long. */
  timeoutMs?: number
}

/** A type as the catalog publishes it: its row, with `id` aliasing `name` and
 *  `schema` resolved from `schema_id`. It is a `RecordType` that also keeps
 *  every column the row has. */
export type CatalogType = TypeRow & { id: string; schema: Schema | null }

export function createCatalog({ query, users: hostUsers, timeoutMs }: CatalogOptions) {
  let current: Catalog | null = null
  let schemasById = new Map<number, Schema>()
  let typesByName = new Map<string, CatalogType>()
  let typesList: CatalogType[] = []
  let usersById = new Map<number, User>()
  let checkedAt = 0

  // A load registers only if no newer load started and no write ended while it
  // was in flight — an older snapshot must never land on top of a newer one.
  let loadGeneration = 0
  let writeGeneration = 0
  let latestLoad: Promise<void> | null = null
  let refreshing: Promise<void> | null = null

  const listeners = new Set<() => void>()

  // Types are published with `schema` resolved from the schemas held here, and
  // `id` aliasing `name` (the reference layer's entity contract — a type value
  // resolves to an entity with an `id`).
  const publishTypes = (): void => {
    if (!current) return
    typesList = current.types.map((t): CatalogType => ({
      ...t,
      id: t.name,
      schema: (t.schema_id != null ? schemasById.get(t.schema_id) : null) ?? null,
    }))
    typesByName = new Map(typesList.map((t) => [t.name, t]))
  }

  const notify = (): void => {
    for (const fn of listeners) fn()
  }

  const register = (catalog: Catalog): void => {
    current = {
      schemas: [...catalog.schemas],
      types: [...catalog.types],
      users: [...catalog.users],
      status: { ...catalog.status },
    }
    schemasById = new Map(current.schemas.map((s) => [s.id, s]))
    usersById = new Map(current.users.map((u) => [u.id, u]))
    publishTypes()
    // A registered copy has not been checked yet: the next refresh asks.
    checkedAt = 0
    registerSchemas((id) => schemasById.get(id) ?? null)
    registerTypes(
      () => typesList,
      (name) => typesByName.get(name)
    )
    registerUsers(hostUsers ?? ((id) => usersById.get(id) ?? null))
    notify()
  }

  const fetch = async (): Promise<Catalog> => {
    const data = await withTimeout(query(fetchCatalog, { withUsers: !hostUsers }), timeoutMs)
    // A transport that does not recognise this operation returns nothing.
    // Reading `.schemas` off it gave a bare TypeError naming neither the query nor the cause.
    if (data == null) {
      throw new Error(
        "fetchCatalog: the transport returned no data — is the operation reaching Hasura?"
      )
    }
    return {
      schemas: (data.schemas ?? []).map(asSchemaRow),
      types: data.types ?? [],
      users: buildDirectory({
        staff: data.directory_staff ?? [],
        partners: data.directory_partners ?? [],
      }),
      status: toStatus(data),
    }
  }

  const fetchStatus = async (): Promise<CatalogStatus> =>
    toStatus(await withTimeout(query(fetchCatalogStatus, {}), timeoutMs))

  const load = (): Promise<void> => {
    const generation = ++loadGeneration
    const writesAtStart = writeGeneration
    const run: Promise<void> = fetch().then((catalog) => {
      // A newer load is in flight: it registers, and its callers are ours.
      if (generation !== loadGeneration) return latestLoad ?? undefined
      // A write ended mid-flight: this snapshot may predate its commit.
      if (writesAtStart !== writeGeneration) return load()
      register(catalog)
      checkedAt = Date.now()
      return undefined
    })
    latestLoad = run
    return run
  }

  /** Ask the database whether the registered copy is current. */
  const check = async (): Promise<"fresh" | "stale"> => {
    if (!current) return "stale"
    const status = await fetchStatus()
    checkedAt = Date.now()
    return current && sameStatus(status, current.status) ? "fresh" : "stale"
  }

  /** Make the registered copy current: nothing when it was checked within
   *  `maxAgeMs`, otherwise check, and load when stale or never loaded.
   *  Concurrent callers share one refresh. `force` loads without checking —
   *  the way out of anything the status cannot see. */
  const refresh = ({
    maxAgeMs = CATALOG_MAX_AGE_MS,
    force = false,
  }: { maxAgeMs?: number; force?: boolean } = {}): Promise<void> => {
    if (force) return load()
    if (current && Date.now() - checkedAt < maxAgeMs) return Promise.resolve()
    if (refreshing) return refreshing
    const run = (async () => {
      if ((await check()) === "fresh") return
      await load()
    })().finally(() => {
      if (refreshing === run) refreshing = null
    })
    refreshing = run
    return run
  }

  /** Apply a schema or type write to the copy, and mark it unchecked so the
   *  next refresh reloads (see the header for why it is not restamped). */
  const written = async (change: CatalogChange): Promise<void> => {
    writeGeneration++
    if (!current) return
    if ("schema" in change) {
      const row = change.schema
      const at = current.schemas.findIndex((s) => s.id === row.id)
      if (at === -1)
        current.schemas = [...current.schemas, row].sort((a, b) => a.name.localeCompare(b.name))
      else current.schemas = current.schemas.map((s, i) => (i === at ? row : s))
      schemasById.set(row.id, row as Schema)
    } else if ("removedSchemaId" in change) {
      current.schemas = current.schemas.filter((s) => s.id !== change.removedSchemaId)
      schemasById.delete(change.removedSchemaId)
    } else {
      const row = change.type
      const at = current.types.findIndex((t) => t.name === row.name)
      const types =
        at === -1 ? [...current.types, row] : current.types.map((t, i) => (i === at ? row : t))
      current.types = types.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    }
    publishTypes()
    // A status no database reports: the next check calls the copy stale. It is
    // persisted with the copy, so a host that saves it reloads on next boot too.
    current.status = UNCHECKED
    checkedAt = 0
    notify()
  }

  return {
    load,
    refresh,
    check,
    register,
    fetch,
    written,
    /** The registered snapshot, for a host to persist. Null until registered. */
    snapshot: (): Catalog | null => current,
    status: (): CatalogStatus | null => current?.status ?? null,
    checkedAt: (): number => checkedAt,
    schemas: (): Schema[] => current?.schemas ?? [],
    types: (): CatalogType[] => typesList,
    users: (): User[] => current?.users ?? [],
    /** Called after every register and every write. Returns an unsubscribe. */
    onChange: (fn: () => void): (() => void) => {
      listeners.add(fn)
      return () => listeners.delete(fn)
    },
  }
}

export type CatalogService = ReturnType<typeof createCatalog>
